import { Loader2 } from "lucide-react";
import { FormProvider } from "react-hook-form";
import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import LoadingOverlay from "../shared/components/LoadingOverlay";
import UserAvatarUpload from "../shared/components/UserAvatarUpload";
import UserFormFields from "../shared/components/UserFormFields";
import { useUserEditViewModel } from "./hooks/useUserEditViewModel";
import { UserEditModalProps } from "./types/userEditView.types";

/**
 * Tailwind rebuild of the legacy `UserEditModal`
 * (src/screens/user/userEdit/UserEditModal.tsx), on the same shell as
 * `CompanyEditModal`. Shares `UserFormFields` with the register drawer;
 * state/submit logic lives in `useUserEditViewModel`.
 */
const UserEditModal = ({ visibleModal, id, roles, disableEmail, closeModal, onSuccess }: UserEditModalProps) => {
  const vm = useUserEditViewModel({ visibleModal, id, closeModal, onSuccess });

  return (
    <>
      <TailwindSideModal
        open={visibleModal}
        onOpenChange={open => {
          if (!open) vm.onCancel();
        }}
        eyebrow="Users"
        title="Edit user"
        titleClassName="tw-font-display"
        description="Update the user's contact details, role and profile picture."
      >
        <FormProvider {...vm.methods}>
          <CommonSplitContainer
            currentHeight={0.86}
            overflowX="hidden"
            topChild={
              <div>
                {vm.isLoadingDetails ? (
                  <div className="tw-flex tw-items-center tw-justify-center tw-py-24">
                    <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-primary" />
                  </div>
                ) : (
                  <UserFormFields
                    control={vm.methods.control}
                    roles={roles}
                    rolePolicy={vm.rolePolicy}
                    disableEmail={disableEmail}
                    avatarSlot={
                      <UserAvatarUpload
                        imageUrl={vm.profileUrl}
                        initial={vm.initial}
                        isBusy={vm.isAvatarBusy}
                        onUpload={vm.onAvatarUpload}
                        onRemove={vm.onAvatarRemove}
                      />
                    }
                  />
                )}
              </div>
            }
            bottomChild={
              <TailwindBottomButtonView
                cancelButtonStatus
                saveButtonStatus
                saveTitle="Save changes"
                buttonSize="lg"
                saveLoading={vm.isSaving}
                saveDisable={vm.isLoadingDetails || vm.isSaving}
                cancelButton={vm.onCancel}
                saveButton={vm.onSave}
              />
            }
          />
        </FormProvider>
      </TailwindSideModal>

      {vm.isSaving ? <LoadingOverlay /> : null}
    </>
  );
};

export default UserEditModal;
